import {
    AuthContext,
    ChatPanel,
    ComposePanel,
    FilterMethod,
    IComment,
    ICustomFilter,
    IMessage,
    ITableFetchOptions,
    ListProviderMessagesOrderEnum,
    MessageList,
    MessagingProvider,
    ProviderClaim,
    ProviderMessageSortType,
    ToastContext,
} from '@enter/core';
import { DataTableCard, PageLayout, PageLayoutHeader, PageLayoutRow, PageLayoutTitle, usePdfView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageDetail, IMessageListingRequest } from 'stores';
import * as S from './messaging.styles';
import claimsStore from "@enter/support/src/stores/claims.store";

interface IMatchParams {
    messageId: string;
}

type IMessagingProps = IInjectedRootStore & RouteComponentProps<IMatchParams>;

export const Messaging: React.FunctionComponent<IMessagingProps> = inject('rootStore')(
    observer((props: IMessagingProps) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        document.title = 'Provider Dashboard - Messaging';
        const { history } = props;

        const authContext = useContext(AuthContext);
        const toastContext = useContext(ToastContext);
        const dialogContext = useContext(DialogContext);
        const [claims, setClaims] = useState<ProviderClaim[] | undefined>();
        const [isDetailLoading, setDetailLoading] = useState<boolean>();
        const [isListLoading, setListLoading] = useState<boolean>();
        const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [messageId, setMessageId] = useState<string | undefined>();
        const [messageDetail, setMessageDetail] = useState<IMessageDetail | undefined>();
        const [openFile] = usePdfView();

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = true;

        useEffect(() => {
            return () => {
                authContext.cancelGroup && authContext.cancelGroup('messaging');
            };
        }, [authContext]);

        const buildRequest = (showDone: boolean, newFetchData: ITableFetchOptions): IMessageListingRequest => {
            const { pageSize, pageIndex, sortBy, validFilters } = newFetchData;
            const sort: ProviderMessageSortType[] = [];
            const order: ListProviderMessagesOrderEnum[] = [];
            _.forEach(sortBy, rule => {
                let s;
                switch (rule.id) {
                    case 'From':
                        s = ProviderMessageSortType.AUTHOR;
                        break;
                    case 'Subject':
                        s = ProviderMessageSortType.SUBJECT;
                        break;
                    case 'Date':
                        s = ProviderMessageSortType.UPDATED;
                        break;
                    case 'Purpose':
                        s = ProviderMessageSortType.PURPOSE;
                        break;
                    default:
                        s = undefined;
                }
                if (s) {
                    sort.push(s);
                    order.push(
                        rule.desc ? ListProviderMessagesOrderEnum.DESCENDING : ListProviderMessagesOrderEnum.ASCENDING,
                    );
                }
            });
            const doneFilter: ICustomFilter = {
                filterId: 'filterOpen',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: showDone ? '1' : '0',
            };
            return {
                page: pageIndex,
                pageSize,
                sort,
                order,
                filters: [...validFilters, doneFilter],
            };
        };

        const refresh = async (): Promise<void> => {
            if (fetchOptions) {
                // force refresh
                handleFetchData();
            }
        };

        const handleFetchData = async (newFetchOptions?: ITableFetchOptions): Promise<void> => {
            const options = newFetchOptions || fetchOptions;
            if (options) {
                try {
                    setListLoading(true);
                    authContext.setGroup && authContext.setGroup('messaging');
                    authContext.cancelGroup && authContext.cancelGroup('messaging');

                    const inboxReq = buildRequest(false, options);
                    const doneReq = buildRequest(true, options);
                    await Promise.all([
                        rootStore.messageStore.loadMessageListing(inboxReq),
                        rootStore.messageStore.loadMessageListing(doneReq),
                    ]);
                    setFetchOptions(options);
                    setListLoading(false);
                } catch (err) {
                    dialogContext.showError(err);
                }
            }
        };

        const refreshMessageDetail = async (mid: string, forceFetch?: boolean): Promise<void> => {
            if (mid === 'compose') {
                return;
            }
            authContext.setGroup && authContext.setGroup('messaging');
            authContext.cancelGroup && authContext.cancelGroup('messaging');

            const detail = await rootStore.messageStore.loadMessageDetail(mid, forceFetch);
            if (detail && !_.isEqual(detail, messageDetail)) {
                setMessageDetail(detail);
                setMessageId(mid);
                setDetailLoading(false);
            }
        };

        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            setClaims([]);
            const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
            setClaims(results);
            return results;
            // return new Promise((resolve, reject) => {
            //     debouncedDoSearch(term, resolve, reject);
            // });
        };

        const debouncedDoSearch = _.debounce(async (term, resolve, reject) => {
            try {
                setClaims([]);
                const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
                setClaims(results);
                resolve(results);  // Resolve the promise with the results
            } catch (error) {
                reject(error);  // Reject the promise if there's an error
            }
        }, 800);


        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            authContext.setGroup && authContext.setGroup('messaging-claim-search');
            authContext.cancelGroup && authContext.cancelGroup('messaging-claim-search');
            console.log("main messaging claimSearch");
            return await doSearch(term);
        };
        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                await refreshMessageDetail(mid, true);
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const composeMessage = async ({ subject, body, claimId, files }: IMessage): Promise<void> => {
            try {
                await rootStore.messageStore.composeMessage(rootStore.account, subject, body, claimId, files);
                await refresh();
                toastContext.notify?.('Message sent', 'left');
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };
        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsRead(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const messageOpen = async (mid: string): Promise<void> => {
            setMessageId(mid);
            setDetailLoading(true);

            // mark read without waiting
            rootStore.messageStore.markAsRead(mid);

            // NB: pre-fill with message info from listing
            const message = rootStore.messageStore.findMessage(mid);
            setMessageDetail({ message });

            history.replace(`/messaging/${mid}`);
        };
        const messageClose = async (): Promise<void> => {
            setMessageId(undefined);
            history.replace(`/messaging`);
        };

        const showCompose = (): void => {
            history.push('/messaging/compose');
        };

        const renderMessageList = (routeProps: RouteComponentProps<IMatchParams>): React.ReactElement | undefined => {
            const { doneTotal, doneMessages, inboxTotal, inboxMessages, unreadInboxTotal } = rootStore.messageStore;
            return (
                <>
                    <PageLayout>
                        <PageLayoutRow>
                            <PageLayoutHeader>
                                <PageLayoutTitle>Messaging</PageLayoutTitle>
                            </PageLayoutHeader>
                        </PageLayoutRow>
                        <PageLayoutRow>
                            <DataTableCard size="large">
                                <S.Wrapper>
                                    <MessageList
                                        doneCount={doneTotal}
                                        doneMessages={doneMessages}
                                        inboxCount={inboxTotal}
                                        inboxMessages={inboxMessages}
                                        unreadCount={unreadInboxTotal}
                                        onFetchData={handleFetchData}
                                        onShowCompose={showCompose}
                                        showFiltering={true}
                                        showNavigation={true}
                                        showSettings={true}
                                        selectedMessageId={messageId}
                                    />
                                </S.Wrapper>
                            </DataTableCard>
                        </PageLayoutRow>
                    </PageLayout>
                </>
            );
        };

        const renderComposePanel = (routeProps: RouteComponentProps<IMatchParams>): React.ReactElement | undefined => {
            const claimId = _.get(history, 'location.state.claimId');
            const subject = _.get(history, 'location.state.subject');
            const body = _.get(history, 'location.state.body');
            if (!isReadOnly) {
                return <ComposePanel claimId={claimId} subject={subject} body={body} hideNote={true} />;
            }
        };

        const renderChatPanel = (routeProps: RouteComponentProps<IMatchParams>) => {
            const mid = _.get(routeProps, 'match.params.messageId');
            if (!mid || mid === 'compose') {
                return;
            }

            return <ChatPanelImpl onRefresh={refreshMessageDetail} messageId={mid} />;
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isListLoading}
                isDetailLoading={isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetail ? messageDetail.message : undefined}
                comments={messageDetail ? messageDetail.comments : undefined}
                documents={messageDetail ? messageDetail.documents : undefined}
                claims={claims}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMessageClose={messageClose}
                onMessageOpen={messageOpen}>
                <Route path="/messaging" render={renderMessageList} />
                <Route path="/messaging/compose" render={renderComposePanel} exact={true} />
                <Route path="/messaging/:messageId" render={renderChatPanel} exact={true} />
            </MessagingProvider>
        );
    }),
);

const ChatPanelImpl: React.FC<{ messageId: string; onRefresh(mid: string, forceFetch?: boolean): void }> = ({
    messageId,
    onRefresh,
}) => {
    useEffect(() => {
        onRefresh(messageId, true);
    }, [messageId]);

    return <ChatPanel hideNote={true} />;
};
